import { SelectorOrNestedKey } from './types'
import { selectorOrNestedKeyToKey } from './helpers'

type OnlyObjectKeys<T> = {
  [P in keyof T]: T[P] extends object ? P : never
}[keyof T]

type OnlyTopLevelObjectKeys<T> = {
  [P in OnlyObjectKeys<T>]: never
}

export interface IFields<T extends object> {
  add(selector: SelectorOrNestedKey<T>): this
  addAllOf(selector: SelectorOrNestedKey<OnlyTopLevelObjectKeys<T>>): this
  toString(): string
}

class Fields<T extends object> implements IFields<T> {
  private fields: string[] = []

  /**
   * Adds a field to be used in the fields parameter.
   */
  add(selector: SelectorOrNestedKey<T>): this {
    this.fields.push(selectorOrNestedKeyToKey(selector))

    return this
  }
  addAllOf(selector: SelectorOrNestedKey<OnlyTopLevelObjectKeys<T>>): this {
    this.fields.push(`${selectorOrNestedKeyToKey(selector)}.*`)

    return this
  }

  /**
   * Returns the stringified version of the fields ready to be used as the fields parameter.
   */
  toString(): string {
    return this.fields.join(',')
  }
}

/**
 * Fields helper for List
 *
 * @example
 * interface AccountList {
 *   Account: {
 *     Age: number
 *     FirstName: string
 *   }
 * }
 *
 * // This results in a string `Account.FirstName,Account.Age`
 * const f = fields<AccountList>()
 *   .add(o => o.Account.FirstName)
 *   .add(o => o.Account.Age)
 *   .toString()
 */
export function fields<T extends object>(): IFields<T> {
  return new Fields()
}
